import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Modal } from "./Modal";
import { formatApiErrorDetail } from "../lib/format";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", pay_method: "", pay_handle: "", status: "active", notes: "" };

export function StudentForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState("");
  useEffect(() => { setForm(initial ? { ...EMPTY, ...initial } : EMPTY); setEmailError(""); }, [initial, open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const email = (form.email || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setBusy(true);
    try {
      const payload = { ...form };
      if (initial?.id) await api.put(`/students/${initial.id}`, payload);
      else await api.post("/students", payload);
      toast.success(initial?.id ? "Student updated." : "Student added.");
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const mentionsEmail =
        (Array.isArray(detail) && detail.some((d) => (d.loc || []).includes("email"))) ||
        (typeof detail === "string" && detail.toLowerCase().includes("email"));
      if (err.response?.status === 422 && mentionsEmail) setEmailError("Please enter a valid email address.");
      else toast.error(formatApiErrorDetail(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? "Edit student" : "Add student"} testid="student-form-modal"
      footer={<>
        <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-brass text-sm" data-testid="student-save">{busy ? "Saving…" : "Save"}</button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="field-label">First name</label><input className="field-input" value={form.first_name} onChange={set("first_name")} data-testid="student-first" /></div>
          <div><label className="field-label">Last name</label><input className="field-input" value={form.last_name} onChange={set("last_name")} data-testid="student-last" /></div>
          <div>
            <label className="field-label">Email</label>
            <input className="field-input" value={form.email} onChange={set("email")} data-testid="student-email" />
            {emailError && <p className="font-sans text-xs text-garnet mt-1" data-testid="student-email-error">{emailError}</p>}
          </div>
          <div><label className="field-label">Phone</label><input className="field-input" value={form.phone} onChange={set("phone")} data-testid="student-phone" /></div>
          <div>
            <label className="field-label">Pay method</label>
            <select className="field-input" value={form.pay_method} onChange={set("pay_method")} data-testid="student-paymethod">
              <option value="">—</option>
              <option value="Venmo">Venmo</option>
              <option value="Zelle">Zelle</option>
              <option value="PayPal">PayPal</option>
              <option value="Check">Check</option>
            </select>
          </div>
          <div><label className="field-label">Pay handle</label><input className="field-input" value={form.pay_handle} onChange={set("pay_handle")} data-testid="student-payhandle" /></div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={form.status} onChange={set("status")} data-testid="student-status">
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div><label className="field-label">Notes</label><textarea className="field-input" rows={2} value={form.notes} onChange={set("notes")} data-testid="student-notes" /></div>
      </div>
    </Modal>
  );
}
